import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useState } from "react";

import { ErrorBanner } from "@/components/Callout";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { setEmail } from "@/lib/api";

export function EditEmailDialog({
  email,
  onClose,
}: {
  email?: string;
  onClose?: () => void;
}) {
  const [addAddEmailDialogOpen, setAddAddEmailDialogOpen] = useState(false);
  const onOpenChange = (open: boolean) => {
    setAddAddEmailDialogOpen(open);

    if (!open) {
      onClose?.();
    }
  };

  const mutation = useMutation({
    onSuccess: () => onOpenChange(false),
    onError: (error: AxiosError) => console.error(error),
    mutationFn: (options: { email: string }) => setEmail(options.email),
  });

  const action = email ? "Update" : "Add";

  return (
    <Dialog open={addAddEmailDialogOpen} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline">{action}</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{action} Email</DialogTitle>
          <DialogDescription>
            Add an email to get updates about your account.
          </DialogDescription>
        </DialogHeader>
        <Separator />
        {mutation.error && <ErrorBanner error={mutation.error} />}
        <form
          className="flex flex-col gap-8"
          onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.target as HTMLFormElement);
            const email = formData.get("email") as string;
            mutation.mutate({ email });
          }}
        >
          <div className="flex flex-col gap-4">
            <Label htmlFor="email">Email</Label>
            <Input id="email" name="email" type="email" defaultValue={email} />
          </div>

          <div className="flex gap-4">
            <div className="flex-1" />
            <Button onPress={() => onOpenChange(false)} variant="outline">
              Cancel
            </Button>
            <Button type="submit" isLoading={mutation.isPending}>
              Submit
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
